import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const Services = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [homeContent, setHomeContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Icon mapping for services - simplified for inline use
  const iconStyles = {
    '🔮': { bg: 'from-purple-400 to-indigo-500', light: 'from-purple-50 to-indigo-50' },
    '✨': { bg: 'from-violet-400 to-purple-500', light: 'from-violet-50 to-purple-50' },
    '⭐': { bg: 'from-blue-400 to-cyan-500', light: 'from-blue-50 to-cyan-50' },
    '💕': { bg: 'from-rose-400 to-pink-500', light: 'from-rose-50 to-pink-50' },
    '❤️': { bg: 'from-rose-400 to-pink-500', light: 'from-rose-50 to-pink-50' },
    '💎': { bg: 'from-emerald-400 to-teal-500', light: 'from-emerald-50 to-teal-50' },
    '🎯': { bg: 'from-amber-400 to-orange-500', light: 'from-amber-50 to-orange-50' },
    '🌟': { bg: 'from-yellow-400 to-amber-500', light: 'from-yellow-50 to-amber-50' }
  };

  // Fetch services and home content from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch services
        const servicesResponse = await axios.get('/api/services');
        if (servicesResponse.data.success) {
          setServices(servicesResponse.data.allServices || []);
        }

        // Fetch home content for CTA section
        const homeResponse = await axios.get('/api/home-content');
        if (homeResponse.data.success) {
          setHomeContent(homeResponse.data.content);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get color scheme based on icon
  const getColorScheme = (icon) => {
    return iconStyles[icon] || { bg: 'from-purple-400 to-indigo-500', light: 'from-purple-50 to-indigo-50' };
  };

  // Handle booking navigation (existing functionality)
  const handleBooking = (service) => {
    navigate('/booking', { state: { selectedService: service } });
  };

  // Handle contact navigation
  const handleContact = () => {
    navigate('/contact');
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom right, #f3e8ff, #ffffff, #e0e7ff)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', margin: '0 auto 16px', animation: 'spin 1s linear infinite', color: '#9333ea' }}>✨</div>
          <p style={{ color: '#4b5563' }}>{t('loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  const currentLang = i18n.language || 'pt';

  // Default CTA text if homeContent is not loaded
  const ctaTitle = homeContent?.cta?.title?.[currentLang] || 
    (currentLang === 'pt' ? 'Não tem certeza de qual serviço escolher?' : 'Not Sure Which Service to Choose?');
  
  const ctaDescription = homeContent?.cta?.description?.[currentLang] || 
    (currentLang === 'pt' 
      ? 'Entre em contato para uma consulta gratuita e ajudarei você a encontrar a orientação perfeita para sua jornada'
      : 'Contact me for a free consultation and I\'ll help you find the perfect guidance for your journey'
    );

  const ctaButtonText = homeContent?.hero?.ctaText?.[currentLang] || 
    (currentLang === 'pt' ? 'Agendar Consulta Gratuita' : 'Schedule Free Consultation');

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right, #f3e8ff, #ffffff, #e0e7ff)' }}>
      {/* Hero Section - Match About/Contact colors */}
      <div style={{ position: 'relative', background: 'linear-gradient(135deg, #6B46C1, #D4AF37)', color: 'white', padding: '80px 16px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 'clamp(2.5rem, 5vw, 3.75rem)', fontWeight: 'normal', margin: '0 0 16px 0', letterSpacing: '0.05em' }}>
          {t('services.title')}
        </h1>
        <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', margin: 0 }}>
          {t('services.subtitle')}
        </p>
      </div>

      {/* Services Grid */}
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '64px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 500px), 1fr))', gap: '32px' }}>
          {services.map((service) => {
            const colorScheme = getColorScheme(service.icon);
            const isHovered = hoveredCard === service._id;

            return (
              <div
                key={service._id}
                onMouseEnter={() => setHoveredCard(service._id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{ position: 'relative' }}
              >
                {/* Card */}
                <div style={{
                  position: 'relative',
                  background: 'white',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  transition: 'all 0.5s',
                  border: isHovered ? '2px solid #d8b4fe' : '2px solid #e9d5ff',
                  boxShadow: isHovered ? '0 25px 50px -12px rgba(147, 51, 234, 0.3)' : '0 10px 15px -3px rgba(0,0,0,0.1)',
                  transform: isHovered ? 'scale(1.02)' : 'scale(1)'
                }}>
                  {/* Gradient Header */}
                  <div style={{
                    background: `linear-gradient(to bottom right, var(--tw-gradient-stops))`,
                    backgroundImage: `linear-gradient(135deg, ${colorScheme.light.includes('purple') ? '#f3e8ff' : colorScheme.light.includes('blue') ? '#eff6ff' : colorScheme.light.includes('rose') ? '#fff1f2' : colorScheme.light.includes('emerald') ? '#ecfdf5' : colorScheme.light.includes('amber') ? '#fef3c7' : '#f5f3ff'}, ${colorScheme.light.includes('indigo') ? '#e0e7ff' : colorScheme.light.includes('cyan') ? '#cffafe' : colorScheme.light.includes('pink') ? '#fce7f3' : colorScheme.light.includes('teal') ? '#ccfbf1' : colorScheme.light.includes('orange') ? '#fed7aa' : '#ede9fe'})`,
                    padding: '32px',
                    position: 'relative',
                    overflow: 'hidden',
                    borderBottom: '2px solid #e9d5ff'
                  }}>
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: `linear-gradient(to right, var(--tw-gradient-stops))`,
                      backgroundImage: `linear-gradient(90deg, ${colorScheme.bg.includes('purple') ? '#c084fc, #818cf8' : colorScheme.bg.includes('blue') ? '#60a5fa, #22d3ee' : colorScheme.bg.includes('rose') ? '#fb7185, #f472b6' : colorScheme.bg.includes('emerald') ? '#34d399, #14b8a6' : colorScheme.bg.includes('amber') ? '#fbbf24, #fb923c' : '#a78bfa, #a855f7'})`,
                      opacity: 0.1
                    }}></div>
                    <div style={{
                      position: 'relative',
                      transition: 'transform 0.5s',
                      transform: isHovered ? 'scale(1.1)' : 'scale(1)'
                    }}>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '64px',
                        height: '64px',
                        borderRadius: '16px',
                        background: `linear-gradient(to bottom right, var(--tw-gradient-stops))`,
                        backgroundImage: `linear-gradient(135deg, ${colorScheme.bg.includes('purple') ? '#c084fc, #818cf8' : colorScheme.bg.includes('blue') ? '#60a5fa, #22d3ee' : colorScheme.bg.includes('rose') ? '#fb7185, #f472b6' : colorScheme.bg.includes('emerald') ? '#34d399, #14b8a6' : colorScheme.bg.includes('amber') ? '#fbbf24, #fb923c' : '#a78bfa, #a855f7'})`,
                        color: 'white',
                        marginBottom: '16px',
                        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                        fontSize: '32px'
                      }}>
                        {service.icon}
                      </div>
                      <h3 style={{ fontSize: '30px', fontWeight: 'bold', marginBottom: '4px', color: '#1f2937' }}>
                        {service.name[currentLang]}
                      </h3>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: 'rgba(255,255,255,0.7)',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#4b5563',
                        textTransform: 'uppercase',
                        marginTop: '8px'
                      }}>
                        {service.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div style={{ padding: '24px' }}>
                    <p style={{ color: '#4b5563', marginBottom: '24px', lineHeight: '1.625' }}>
                      {service.description[currentLang]}
                    </p>

                    {/* Features */}
                    {service.features && service.features.length > 0 && (
                      <div style={{ marginBottom: '24px' }}>
                        {service.features.map((feature, i) => (
                          <div 
                            key={i}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '12px',
                              fontSize: '14px',
                              color: '#374151',
                              marginBottom: '12px',
                              opacity: isHovered ? 1 : 0.8,
                              transform: isHovered ? 'translateX(5px)' : 'translateX(0)',
                              transition: `all 0.3s ease ${i * 50}ms`
                            }}
                          >
                            <div style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '9999px',
                              background: `linear-gradient(to right, var(--tw-gradient-stops))`,
                              backgroundImage: `linear-gradient(90deg, ${colorScheme.bg.includes('purple') ? '#c084fc, #818cf8' : colorScheme.bg.includes('blue') ? '#60a5fa, #22d3ee' : colorScheme.bg.includes('rose') ? '#fb7185, #f472b6' : colorScheme.bg.includes('emerald') ? '#34d399, #14b8a6' : colorScheme.bg.includes('amber') ? '#fbbf24, #fb923c' : '#a78bfa, #a855f7'})`,
                              marginTop: '8px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                              flexShrink: 0
                            }}></div>
                            <span>{feature[currentLang]}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Footer */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '24px', borderTop: '1px solid #f3f4f6' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>{currentLang === 'pt' ? 'Duração' : 'Duration'}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#1f2937', fontWeight: '600' }}>
                            <span>⏱️</span>
                            {service.duration} min
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>{currentLang === 'pt' ? 'Investimento' : 'Investment'}</div>
                          <div style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            background: 'linear-gradient(to right, #9333ea, #6366f1)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                          }}>
                            €{service.price}
                          </div>
                        </div>
                      </div>

                      {service.bookingEnabled && (
                        <button 
                          onClick={() => handleBooking(service)}
                          style={{
                            background: `linear-gradient(to right, var(--tw-gradient-stops))`,
                            backgroundImage: `linear-gradient(90deg, ${colorScheme.bg.includes('purple') ? '#c084fc, #818cf8' : colorScheme.bg.includes('blue') ? '#60a5fa, #22d3ee' : colorScheme.bg.includes('rose') ? '#fb7185, #f472b6' : colorScheme.bg.includes('emerald') ? '#34d399, #14b8a6' : colorScheme.bg.includes('amber') ? '#fbbf24, #fb923c' : '#a78bfa, #a855f7'})`,
                            color: 'white',
                            padding: '12px 24px',
                            borderRadius: '12px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.3s',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                            border: 'none',
                            cursor: 'pointer',
                            paddingRight: isHovered ? '16px' : '24px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)';
                          }}
                        >
                          <span>📅</span>
                          {currentLang === 'pt' ? 'Reservar Agora' : 'Book Now'}
                          <span style={{
                            transition: 'all 0.3s',
                            opacity: isHovered ? 1 : 0,
                            transform: isHovered ? 'translateX(0)' : 'translateX(-8px)'
                          }}>→</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* No services message */}
        {services.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 0' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>✨</div>
            <p style={{ color: '#4b5563', fontSize: '18px' }}>
              {currentLang === 'pt' ? 'Nenhum serviço disponível no momento.' : 'No services available at the moment.'}
            </p>
          </div>
        )}
      </div>

      {/* Call to Action - Match About/Contact colors */}
      <div style={{ background: 'linear-gradient(135deg, #6B46C1, #D4AF37)', padding: '64px 0' }}>
        <div style={{ maxWidth: '896px', margin: '0 auto', textAlign: 'center', padding: '0 16px' }}>
          <div style={{ fontSize: '48px', margin: '0 auto 16px', color: '#e9d5ff' }}>✨</div>
          <h2 style={{ fontSize: 'clamp(1.875rem, 4vw, 2.25rem)', fontWeight: 'bold', color: 'white', marginBottom: '16px' }}>
            {ctaTitle}
          </h2>
          <p style={{ color: '#e9d5ff', marginBottom: '32px', fontSize: '18px' }}>
            {ctaDescription}
          </p>
          <button 
            onClick={handleContact}
            style={{
              background: 'white',
              color: '#7c3aed',
              padding: '16px 40px',
              borderRadius: '12px',
              fontWeight: 'bold',
              fontSize: '18px',
              transition: 'all 0.3s',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f3e8ff';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {ctaButtonText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Services;
